import React, { useState } from "react";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../../context/CartContext";
import api from "../../api";

const STATUS_LABELS = {
  pending:   "Received",
  confirmed: "Confirmed",
  preparing: "Being prepared",
  ready:     "Ready",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

function CheckoutForm({ onBack, onSuccess }) {
  const { items, totalPrice, clearCart } = useCart();
  const [form, setForm] = useState({
    clientName: "",
    clientEmail: "",
    phoneNumber: "",
    deliveryMode: "pickup", // "pickup" | "delivery"
    address: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDelivery = form.deliveryMode === "delivery";

  const handle = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.clientName.trim()) {
      setError("Full name is required.");
      return;
    }
    if (!form.phoneNumber.trim()) {
      setError("Phone number is required.");
      return;
    }
    if (isDelivery && !form.address.trim()) {
      setError("Please enter your delivery address.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/orders", {
        clientName:  form.clientName,
        clientEmail: form.clientEmail,
        phoneNumber: form.phoneNumber,
        delivery:    isDelivery,
        address:     isDelivery ? form.address : undefined,
        items: items.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
      });
      const order = res.data?.data ?? res.data;
      clearCart();
      onSuccess(order);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 p-5">
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-primary hover:underline self-start"
      >
        ← Back to cart
      </button>

      <h3 className="font-bold text-lg">Your details</h3>

      {/* Full name — required */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-600">
          Full name <span className="text-red-400">*</span>
        </label>
        <input
          name="clientName"
          value={form.clientName}
          onChange={handle}
          required
          placeholder="e.g. Rayen Ben Saad"
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Email — optional */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-600">
          Email address <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          name="clientEmail"
          type="email"
          value={form.clientEmail}
          onChange={handle}
          placeholder="you@example.com"
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Phone — required */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-600">
          Phone number <span className="text-red-400">*</span>
        </label>
        <input
          name="phoneNumber"
          type="tel"
          value={form.phoneNumber}
          onChange={handle}
          required
          placeholder="+216 XX XXX XXX"
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Pickup / Delivery toggle */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold text-gray-600">Fulfillment</label>
        <div className="grid grid-cols-2 gap-2">
          <label
            className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 cursor-pointer text-sm font-medium transition-colors ${
              !isDelivery
                ? "border-primary bg-primary/10 text-primary"
                : "border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            <input
              type="radio"
              name="deliveryMode"
              value="pickup"
              checked={!isDelivery}
              onChange={handle}
              className="sr-only"
            />
            🏪 Pickup
          </label>
          <label
            className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 cursor-pointer text-sm font-medium transition-colors ${
              isDelivery
                ? "border-primary bg-primary/10 text-primary"
                : "border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            <input
              type="radio"
              name="deliveryMode"
              value="delivery"
              checked={isDelivery}
              onChange={handle}
              className="sr-only"
            />
            🚚 Delivery
          </label>
        </div>
      </div>

      {/* Delivery note + address */}
      {isDelivery && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <span className="text-amber-500 text-base flex-shrink-0 mt-0.5">ℹ️</span>
            <p className="text-xs text-amber-800 leading-relaxed">
              Delivery is available <strong>within Tunis city only</strong>. For further
              information or to confirm delivery to your area, contact us at{" "}
              <a
                href="tel:+21694088552"
                className="font-semibold underline hover:no-underline"
              >
                94 088 552
              </a>
              .
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">
              Delivery address <span className="text-red-400">*</span>
            </label>
            <input
              name="address"
              value={form.address}
              onChange={handle}
              required
              placeholder="Street, area, Tunis"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
      )}

      {error && (
        <p className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="border-t pt-3 flex justify-between font-semibold text-sm">
        <span>Total</span>
        <span className="text-primary">{totalPrice.toFixed(2)} TND</span>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-primary text-white rounded-lg py-3 font-semibold hover:bg-primary/90 transition disabled:opacity-60"
      >
        {loading ? "Placing order…" : "Place order"}
      </button>
    </form>
  );
}

function OrderConfirmation({ order, onClose }) {
  return (
    <div className="flex flex-col items-center gap-4 p-6 text-center">
      <div className="text-5xl">🎉</div>
      <h3 className="font-bold text-xl">Order placed!</h3>
      <p className="text-sm text-gray-500">
        We received your order. You can track it anytime with your order reference.
      </p>
      <div className="bg-primary/10 rounded-lg px-4 py-3 w-full">
        <p className="text-xs text-gray-500 mb-1">Order reference</p>
        <p className="font-bold text-lg text-primary">#{order.id}</p>
        <p className="text-xs mt-1">
          Status:{" "}
          <span className="font-medium">{STATUS_LABELS[order.status] ?? order.status}</span>
        </p>
      </div>
      <p className="text-xs text-gray-400">
        We'll confirm your order by email shortly.
      </p>
      <button
        onClick={onClose}
        className="w-full bg-primary text-white rounded-lg py-3 font-semibold hover:bg-primary/90 transition mt-2"
      >
        Done
      </button>
    </div>
  );
}

export default function CartDrawer({ open, onClose }) {
  const { items, removeItem, updateQty, totalItems, totalPrice } = useCart();
  const [step, setStep] = useState("cart"); // "cart" | "checkout" | "confirmed"
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const handleClose = () => {
    setStep("cart");
    setConfirmedOrder(null);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-50"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2 font-bold text-lg">
            <ShoppingBag size={20} className="text-primary" />
            Cart
            {totalItems > 0 && (
              <span className="bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </div>
          <button onClick={handleClose} aria-label="Close cart">
            <X size={22} className="text-gray-500 hover:text-gray-800" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {step === "confirmed" && confirmedOrder ? (
            <OrderConfirmation order={confirmedOrder} onClose={handleClose} />
          ) : step === "checkout" ? (
            <CheckoutForm
              onBack={() => setStep("cart")}
              onSuccess={(order) => {
                setConfirmedOrder(order);
                setStep("confirmed");
              }}
            />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 py-16">
              <ShoppingBag size={40} />
              <p className="text-sm">Your cart is empty</p>
            </div>
          ) : (
            <ul className="divide-y">
              {items.map(({ product, quantity }) => {
                const img = product.imageUrl || product.image || "";
                return (
                  <li key={product.id} className="flex gap-3 p-4">
                    {img && (
                      <img
                        src={img}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{product.name}</p>
                      <p className="text-primary text-sm font-medium">
                        {Number(product.price).toFixed(2)} TND
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQty(product.id, quantity - 1)}
                          disabled={quantity <= 1}
                          className="w-6 h-6 rounded-full border flex items-center justify-center disabled:opacity-40"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm w-5 text-center">{quantity}</span>
                        <button
                          onClick={() => updateQty(product.id, quantity + 1)}
                          className="w-6 h-6 rounded-full border flex items-center justify-center"
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(product.id)}
                      aria-label="Remove item"
                      className="text-gray-300 hover:text-red-400 self-start pt-1"
                    >
                      <X size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer — only show for cart step */}
        {step === "cart" && items.length > 0 && (
          <div className="border-t px-5 py-4 space-y-3">
            <div className="flex justify-between font-semibold text-sm">
              <span>Total ({totalItems} items)</span>
              <span className="text-primary">{totalPrice.toFixed(2)} TND</span>
            </div>
            <button
              onClick={() => setStep("checkout")}
              className="w-full bg-primary text-white rounded-lg py-3 font-semibold hover:bg-primary/90 transition"
            >
              Checkout
            </button>
          </div>
        )}
      </div>
    </>
  );
}
